import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Info, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const FCR_GUIDANCE_TITLE = 'Important — Correct FCR Model Selection';
const FCR_GUIDANCE = [
  'Selecting the correct genetic origin and FCR model is essential for reliable missing-fish detection.',
  "If your farm's verified historical FCR is worse than the standard model, select a higher FCR percentage (e.g. 110%).",
  'If your historical FCR is better, select a lower percentage (e.g. 90%).',
  '100% represents standard model performance.',
  'Important: If no missing fish are detected, your selected model may not accurately represent your farm. Incorrect model selection can hide potential discrepancies or create false detections.',
  'Use independently verified historical performance to calibrate your model. Never adjust settings simply to produce a desired result.',
];

const FcrGuidanceText = ({ className = '' }) => (
  <div className={`space-y-2 text-sm text-slate-700 ${className}`}>
    {FCR_GUIDANCE.map((p) => (
      <p key={p} className={p.startsWith('Important:') ? 'font-semibold text-amber-800' : ''}>{p}</p>
    ))}
  </div>
);

export const FcrGuidanceInfo = () => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" aria-label="FCR model selection guidance" data-testid="fcr-guidance-info-btn"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-emerald-700 transition-colors hover:bg-emerald-100">
        <Info className="h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-96 max-w-[90vw]" data-testid="fcr-guidance-info-content">
      <p className="mb-2 text-sm font-bold text-slate-900">{FCR_GUIDANCE_TITLE}</p>
      <FcrGuidanceText className="text-xs" />
    </PopoverContent>
  </Popover>
);

// Mandatory one-time acknowledgement; cannot be dismissed without ticking + continuing.
export const FcrGuidanceModal = () => {
  const { user, token, refreshUser } = useAuth();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  if (!user || user.fcr_guidance_ack_at) return null;

  const acknowledge = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/fcr-guidance-ack`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save your acknowledgement. Please try again.');
      setSaving(false);
    }
  };
  const block = (e) => e.preventDefault();

  return (
    <Dialog open>
      <DialogContent data-testid="fcr-guidance-modal" className="max-w-xl [&>button:last-child]:hidden"
        onEscapeKeyDown={block} onPointerDownOutside={block} onInteractOutside={block}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            {FCR_GUIDANCE_TITLE}
          </DialogTitle>
        </DialogHeader>
        <FcrGuidanceText />
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm font-medium text-slate-800">
          <Checkbox checked={checked} onCheckedChange={(v) => setChecked(v === true)} data-testid="fcr-guidance-ack-checkbox" className="mt-0.5" />
          I have read and understood the importance of correct genetic origin and FCR model selection.
        </label>
        <DialogFooter>
          <Button onClick={acknowledge} disabled={!checked || saving} data-testid="fcr-guidance-continue-btn"
            className="bg-emerald-600 text-white hover:bg-emerald-700">
            {saving ? 'Saving…' : 'I Understand — Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
